import type { AnyAction } from "redux";
import * as actionTypes from "./actionTypes.ts";
import * as dashboardTypes from "../actions/types.js";
import type {
  FeedFilterOption,
  FeedPost,
  FeedPortfolio,
  FeedItem,
} from "../../types/feed.ts";
import { normalizePostLikes } from "../../utils/normalizePostLikes.ts";
import { pickPostCommentsFromApi } from "../../utils/pickPostCommentsFromApi.ts";

export type FeedViewState = {
  selectedOption: FeedFilterOption;
  order: string | null;
  type: string | null;
};

export type FeedItemOrderEntry = { type: "post" | "portfolio"; id: number };

export type ExploreFeedState = {
  items: FeedItem[];
  nextCursor: string | null;
  hasMore: boolean;
};

export type FeedState = {
  feedView: FeedViewState;
  posteos: FeedPost[];
  ownPosteos: FeedPost[];
  allPortfolios: FeedPortfolio[];
  userPortfolios: FeedPortfolio[];
  recentPost: FeedPost | null;
  itemOrder: FeedItemOrderEntry[];
  exploreFeed: ExploreFeedState;
};

const INITIAL_EXPLORE: ExploreFeedState = {
  items: [],
  nextCursor: null,
  hasMore: false,
};

const INITIAL_STATE: FeedState = {
  feedView: {
    selectedOption: "Mostrar",
    order: null,
    type: null,
  },
  posteos: [],
  ownPosteos: [],
  allPortfolios: [],
  userPortfolios: [],
  recentPost: null,
  itemOrder: [],
  exploreFeed: INITIAL_EXPLORE,
};

function patchExplorePost(
  items: FeedItem[],
  postId: number | string,
  patch: (p: FeedPost) => FeedPost
): FeedItem[] {
  const idStr = String(postId);
  return items.map((item) => {
    if (item.itemType !== "post") return item;
    if (String((item as FeedPost).id) !== idStr) return item;
    return patch(item as FeedPost);
  });
}

const updatePosteoFromList = (
  state: FeedState,
  posteo: Partial<FeedPost> & { id: number }
): FeedState => {
  const updatedPosteos = (state.posteos || []).map((p) =>
    p.id === posteo.id ? { ...p, ...posteo } : p
  );
  const updatedOwnPosteos = (state.ownPosteos || []).map((p) =>
    p.id === posteo.id ? { ...p, ...posteo } : p
  );
  return { ...state, posteos: updatedPosteos, ownPosteos: updatedOwnPosteos };
};

type MergeLikesMode =
  | { syncLikesCountFromList: true }
  | {
      syncLikesCountFromList: false;
      useRollbackLikedBy: true;
      rollbackLikedByCurrentUser: boolean | undefined;
    }
  | { syncLikesCountFromList: false };

function applyOptimisticLikeToPost(
  p: FeedPost,
  postIdStr: string,
  userId: number | string,
  add: boolean
): FeedPost {
  if (String(p.id) !== postIdStr) return p;
  const list = Array.isArray(p.likes) ? [...p.likes] : [];
  const idx = list.findIndex((l) => String(l?.user?.id) === String(userId));
  if (add) {
    if (idx >= 0) {
      return { ...p, likedByCurrentUser: true };
    }
    const next = [...list, { user: { id: userId } }];
    return {
      ...p,
      likes: next,
      likedByCurrentUser: true,
      likesCount: Math.max(typeof p.likesCount === "number" ? p.likesCount : 0, next.length),
    };
  }
  if (idx >= 0) {
    const next = list.filter((_, i) => i !== idx);
    return {
      ...p,
      likes: next,
      likedByCurrentUser: false,
    };
  }
  if (p.likedByCurrentUser) {
    return { ...p, likedByCurrentUser: false };
  }
  return p;
}

const mergeLikesForPost = (
  state: FeedState,
  postId: number,
  mutate: (likes: FeedPost["likes"]) => FeedPost["likes"],
  mode: MergeLikesMode
): FeedState => {
  const postIdStr = String(postId);
  const patchPost = (p: FeedPost): FeedPost => {
    if (String(p.id) !== postIdStr) return p;
    const nextLikes = mutate(p.likes ?? []) ?? [];
    if (mode.syncLikesCountFromList) {
      return {
        ...p,
        likes: nextLikes,
        likesCount: nextLikes.length,
        likedByCurrentUser: undefined,
      };
    }
    if ("useRollbackLikedBy" in mode && mode.useRollbackLikedBy) {
      return {
        ...p,
        likes: nextLikes,
        likedByCurrentUser: mode.rollbackLikedByCurrentUser,
      };
    }
    return { ...p, likes: nextLikes };
  };
  return {
    ...state,
    posteos: (state.posteos || []).map((p) => patchPost(p)),
    ownPosteos: (state.ownPosteos || []).map((p) => patchPost(p)),
    exploreFeed: {
      ...state.exploreFeed,
      items: patchExplorePost(state.exploreFeed.items, postId, (p) => patchPost(p)),
    },
  };
};

export default function feedReducer(
  state: FeedState = INITIAL_STATE,
  action: AnyAction
): FeedState {
  switch (action.type) {
    case actionTypes.SET_FEED_VIEW: {
      const next = {
        ...state,
        feedView: {
          selectedOption: action.payload?.selectedOption ?? state.feedView.selectedOption,
          order: action.payload?.order ?? state.feedView.order,
          type: action.payload?.type ?? state.feedView.type,
        },
      };
      if (Array.isArray(action.payload?.itemOrder)) {
        next.itemOrder = action.payload.itemOrder;
      }
      return next;
    }
    case actionTypes.SET_FEED_ITEM_ORDER:
      return {
        ...state,
        itemOrder: Array.isArray(action.payload) ? action.payload : [],
      };
    case actionTypes.FEED_EXPLORE_RESET:
      return {
        ...state,
        exploreFeed: INITIAL_EXPLORE,
      };
    case actionTypes.FEED_EXPLORE_SET: {
      const { items, nextCursor, hasMore, append } = action.payload ?? {};
      const newItems = Array.isArray(items) ? (items as FeedItem[]) : [];
      const cursor = nextCursor ?? null;
      const more = Boolean(hasMore);
      if (!append) {
        return {
          ...state,
          exploreFeed: {
            items: newItems,
            nextCursor: cursor,
            hasMore: more,
          },
        };
      }
      const prev = state.exploreFeed.items || [];
      const seen = new Set(prev.map((i) => `${i.itemType}-${i.id}`));
      const merged = [...prev];
      for (const it of newItems) {
        const k = `${it.itemType}-${it.id}`;
        if (!seen.has(k)) {
          seen.add(k);
          merged.push(it);
        }
      }
      return {
        ...state,
        exploreFeed: {
          items: merged,
          nextCursor: cursor,
          hasMore: more,
        },
      };
    }
    case dashboardTypes.GET_ALL_PORTFOLIOS:
      return { ...state, allPortfolios: action.payload ?? [] };
    case dashboardTypes.GET_USER_PORTFOLIOS:
      return { ...state, userPortfolios: action.payload ?? [] };
    case dashboardTypes.GET_POSTEOS:
      return { ...state, posteos: action.payload ?? [] };
    case dashboardTypes.GET_OWN_POSTEOS:
      return { ...state, ownPosteos: action.payload ?? [] };
    case dashboardTypes.RECENT_POST:
      return { ...state, recentPost: action.payload ?? null };
    case dashboardTypes.UPDATE_POSTEO_INFO: {
      const posteo = action.payload?.posteo ?? action.payload;
      const base = updatePosteoFromList(state, posteo);
      if (posteo?.id == null) return base;
      return {
        ...base,
        exploreFeed: {
          ...base.exploreFeed,
          items: patchExplorePost(base.exploreFeed.items, Number(posteo.id), (p) => ({
            ...p,
            ...posteo,
          })),
        },
      };
    }
    case dashboardTypes.GET_POSTEO_WITH_COMMENTS: {
      const { post, comments } = action.payload ?? {};
      if (!post) return state;
      const normComments = Array.isArray(comments) ? comments : [];
      const count = normComments.length;
      const mergeInto = (prev: FeedPost): FeedPost => ({
        ...prev,
        ...(post as FeedPost),
        comments: normComments,
        commentsCount: count,
        comments_count: count,
      });
      return {
        ...state,
        posteos: (state.posteos || []).map((p) =>
          String(p.id) === String(post.id) ? mergeInto(p) : p
        ),
        ownPosteos: (state.ownPosteos || []).map((p) =>
          String(p.id) === String(post.id) ? mergeInto(p) : p
        ),
        exploreFeed: {
          ...state.exploreFeed,
          items: patchExplorePost(state.exploreFeed.items, post.id, (prev) => ({
            ...mergeInto(prev),
            itemType: "post" as const,
          })),
        },
      };
    }
    case actionTypes.FEED_OPTIMISTIC_LIKE_POST: {
      const { postId, userId, add } = action.payload ?? {};
      if (postId == null || userId == null) return state;
      const postIdStr = String(Number(postId));
      const mapPost = (p: FeedPost) => applyOptimisticLikeToPost(p, postIdStr, userId, add);
      return {
        ...state,
        posteos: (state.posteos || []).map((p) => mapPost(p)),
        ownPosteos: (state.ownPosteos || []).map((p) => mapPost(p)),
        exploreFeed: {
          ...state.exploreFeed,
          items: state.exploreFeed.items.map((item) =>
            item.itemType === "post" ? mapPost(item as FeedPost) : item
          ),
        },
      };
    }
    case actionTypes.FEED_MERGE_POST_PARTIAL: {
      const payload = action.payload ?? {};
      const { postId, likes } = payload;
      if (postId == null) return state;
      const id = Number(postId);
      if (likes === undefined) return state;
      const list = Array.isArray(likes) ? likes : [];
      const syncFromServer = payload.syncFromServer !== false;
      if (syncFromServer) {
        return mergeLikesForPost(state, id, () => list, { syncLikesCountFromList: true });
      }
      if (payload.useRollbackLikedBy === true) {
        return mergeLikesForPost(state, id, () => list, {
          syncLikesCountFromList: false,
          useRollbackLikedBy: true,
          rollbackLikedByCurrentUser: payload.rollbackLikedByCurrentUser,
        });
      }
      return mergeLikesForPost(state, id, () => list, { syncLikesCountFromList: false });
    }
    case actionTypes.FEED_PREPEND_POST: {
      const post = action.payload;
      if (!post || post.id == null) return state;
      const rawImages = Array.isArray(post.images) ? post.images : [];
      const images = rawImages.map((img) =>
        typeof img === "string" ? { image: img } : img
      );
      const normalized = {
        ...post,
        comments: pickPostCommentsFromApi(post),
        likes: normalizePostLikes(post.likes),
        images,
        video: Array.isArray(post.video) ? post.video : [],
      } as FeedPost;
      const entry: FeedItemOrderEntry = { type: "post", id: Number(post.id) };
      const exploreEntry: FeedItem = { ...normalized, itemType: "post" };
      return {
        ...state,
        posteos: [normalized, ...(state.posteos || [])],
        ownPosteos: [normalized, ...(state.ownPosteos || [])],
        itemOrder: [entry, ...(state.itemOrder || [])],
        exploreFeed: {
          ...state.exploreFeed,
          items: [
            exploreEntry,
            ...state.exploreFeed.items.filter(
              (i) =>
                !(i.itemType === "post" && String(i.id) === String(normalized.id))
            ),
          ],
        },
      };
    }
    case actionTypes.FEED_MERGE_POSTS_COMMENTS: {
      const commentsByPostId = action.payload?.commentsByPostId as
        | Record<number, FeedPost["comments"]>
        | undefined;
      if (!commentsByPostId || typeof commentsByPostId !== "object") return state;
      const applyComments = (p: FeedPost): FeedPost =>
        commentsByPostId[p.id] !== undefined
          ? { ...p, comments: commentsByPostId[p.id] }
          : p;
      return {
        ...state,
        posteos: (state.posteos || []).map(applyComments),
        ownPosteos: (state.ownPosteos || []).map(applyComments),
        exploreFeed: {
          ...state.exploreFeed,
          items: state.exploreFeed.items.map((item) => {
            if (item.itemType !== "post") return item;
            const c = commentsByPostId[item.id];
            if (c === undefined) return item;
            return { ...item, comments: c };
          }),
        },
      };
    }
    default:
      return state;
  }
}
